import { create } from 'zustand';
import { MANUAL_AD_STRATEGY_ID } from 'src/utils/app';

export const adStrategies = [
  {
    id: 'MaximizeSales',
    label: 'Maximize Sales'
  },
  {
    id: 'MaximizeClicks',
    label: 'Maximize Clicks'
  },
  {
    id: 'MaximizeIncrementalSales',
    label: 'Maximize Incremental Sales'
  },
  {
    id: 'Manual',
    label: 'Manual (No Automation)'
  }
];

export enum Error {
  Budget,
  Name,
  Entity
}

interface PortfolioBuilderProps {
  portfolioName: string;
  budget: number;
  selectedOptimizationId: string;
  selectedEntityId: string;
  error: Error[];
  selectedCampaignIds: Set<string>;
  campaignIds: Set<string>;
  searchTerm: string;
  campaignData: Record<string, { campaignId: string; campaignName: string }>;

  selectCampaignId: (campaignId: string) => void;
  unselectCampaignId: (campaignId: string) => void;
  setSelectedEntityId: (selectedEntityId: string) => void;
  setPortfolioName: (name: string) => void;
  setBudget: (budget: number) => void;
  setSelectedOptimizationId: (selectedOptimizationDropdown: string) => void;
  setError: (error: Error) => void;
  setCampaignIds: (campaignIds: Set<string>) => void;
  setCampaignData: (campaignData: Record<string, { campaignId: string; campaignName: string }>) => void;
  addAllCampaignIds: () => void;
  removeAllCampaignIds: () => void;
  setSearchTerm: (searchTerm: string) => void;
}

const initialState = {
  portfolioName: '',
  budget: null,
  selectedOptimizationId: MANUAL_AD_STRATEGY_ID,
  selectedEntityId: null,
  error: [],
  selectedCampaignIds: new Set<string>(),
  campaignIds: new Set<string>(),
  searchTerm: '',
  campaignData: {}
};

const usePortfolioBuilderStore = create<PortfolioBuilderProps>((set) => ({
  ...initialState,
  setSelectedEntityId: (selectedEntityId: string) => set({ selectedEntityId }),
  setPortfolioName: (name: string) => set({ portfolioName: name }),
  setBudget: (budget: number) => set({ budget }),
  setCampaignIds: (campaignIds: Set<string>) => set({ campaignIds }),
  setCampaignData: (campaignData) => set({ campaignData }),
  setSearchTerm: (searchTerm: string) => set({ searchTerm }),
  setError: (error: Error) => {
    set((state) => ({ error: [...state.error, error] }));
    setTimeout(() => {
      set({ error: [] });
    }, 2000);
  },
  selectCampaignId: (campaignId: string) => {
    set((state) => {
      const newSelectedCampaignIds = new Set(state.selectedCampaignIds);
      newSelectedCampaignIds.add(campaignId);
      return { selectedCampaignIds: newSelectedCampaignIds };
    });
  },
  unselectCampaignId: (campaignId: string) => {
    set((state) => {
      const newSelectedCampaignIds = new Set(state.selectedCampaignIds);
      newSelectedCampaignIds.delete(campaignId);
      return { selectedCampaignIds: newSelectedCampaignIds };
    });
  },
  addAllCampaignIds: () => {
    set((state) => {
      // Convert campaignIds Set to array
      const campaignIdsArray = Array.from(state.campaignIds);
      
      // If there's a search term, filter the campaigns by name
      let filteredCampaignIds;
      if (state.searchTerm) {
        const searchTermLower = state.searchTerm.toLowerCase();
        filteredCampaignIds = campaignIdsArray.filter(id => {
          const campaign = state.campaignData[id];
          return campaign && campaign.campaignName && 
                 campaign.campaignName.toLowerCase().includes(searchTermLower);
        });
      } else {
        filteredCampaignIds = campaignIdsArray;
      }
      
      return { selectedCampaignIds: new Set(filteredCampaignIds) };
    });
  },
  removeAllCampaignIds: () => set({ selectedCampaignIds: new Set<string>() }),
  setSelectedOptimizationId: (selectedOptimizationDropdown: string) =>
    set({ selectedOptimizationId: selectedOptimizationDropdown })
}));

export default usePortfolioBuilderStore;
